import { LoginOptionsConfiguration } from "@remo-co/types";
import { IServicePlanSettings } from "modules/event/types";
import { CompanyFeatureToggles } from "./constants";

export interface ICompany {
  _id: string;
  id: string;
  name: string;
  code: string;
  createdAt: string;
  logoURL?: string;
  enabledWhiteLabel?: boolean;
  plan?: ICompanyServicePlan;
  companySettings?: ICompanySettings;
  owner: string;
  domain: string;
  updatedAt?: Date;
}

type ProviderType = "saml2" | "social-network" | "openid";
export type CompanyFeatureToggle =
  | CompanyFeatureToggles.Chat
  | CompanyFeatureToggles.ChatUpload
  | CompanyFeatureToggles.Polls
  | CompanyFeatureToggles.LockTables
  | CompanyFeatureToggles.QA
  | CompanyFeatureToggles.Whiteboard;
export type CompanyFeatureRecord = { [key in CompanyFeatureToggle]?: boolean };

export interface ICompanyServicePlan {
  name: string;
  chargebeePlanId?: string;
  appsumoPlanId?: string;
  settings: IServicePlanSettings;
  trialExpired: boolean;
  trialLeft: number;
  trialStartedAt: Date | null;
  planId: string;
  billingStart: Date | null;
  billingCreated: Date | null;
  billingEnd: Date | null;
  isAgencyPlan: boolean;
  guestPrice?: number;
  agencyTier?: number;
  isEnterprisePlan?: boolean;
  isOneTimePlan?: boolean;
  paymentFailedAt: Date | null;
  coupon?: string;
  term: string;
  guestPriceCurrency?: string;
  disableUpgrade: boolean; // To identify if it is legacy/deprecated plan
  level: number; // to identify the level to check if user is upgrading/downgrading
}

export type BoardProvider = "MIRO" | null;

export interface IWebhookSettings {
  url: string;
  event: string;
}

export interface IIdentityProvider {
  name: string;
  type: ProviderType;
  providerId: string;
  isEnabled?: boolean;
}

export interface IEventcubeSettings {
  storeId: string;
  appToken: string;
  currency: string;
}

export enum CustomEmailSenderState {
  AWAITING_CONFIRMATION = "awaiting-confirmation",
  REQUESTED = "requested",
  VERIFIED = "verified",
}

export interface ICustomEmailSenderSettings {
  email: string;
  replyTo?: string;
  fromName: string;
  status: CustomEmailSenderState;
}

export interface ICompanySettings {
  company: string;
  defaultOnboardText?: string;
  defaultOnboardMedia?: IOnboardMediaSettings;
  exitURL?: string;
  disableVPNCheck?: boolean;
  discoveryDisabledByDefault?: boolean;
  companyBadgeURL?: string;
  enableCompanyBadge?: boolean;
  boardProvider?: BoardProvider;
  isMiroBoardEnabled?: boolean;
  templates?: string[];
  identityProviders?: IIdentityProvider;
  disabledFeatures?: CompanyFeatureToggle[];
  loginOptions?: LoginOptionsConfiguration;
  enableAgencyWhiteLabelling?: boolean;
  whiteLabelLogoURL?: string;
  whiteLabelFaviconURL?: string;
  whiteLabelSignInBackgroundURL?: string;
  customDomain?: string;
  isCustomDomainVerified?: boolean;
  disableFreshchatWidget?: boolean;
  eventcubeSettings?: IEventcubeSettings;
  emailSenderSettings?: ICustomEmailSenderSettings;
  webhooks?: IWebhookSettings[];
}

export type CompanyPublicSettings = {
  identityProviders?: IIdentityProvider;
  disableVPNCheck?: boolean;
  discoveryDisabledByDefault?: boolean;
  loginOptions: LoginOptionsConfiguration;
  enableAgencyWhiteLabelling?: boolean;
  whiteLabelLogoURL?: string;
  whiteLabelFaviconURL?: string;
  whiteLabelSignInBackgroundURL?: string;
  customDomain?: string;
  isCustomDomainVerified?: boolean;
  disableFreshchatWidget?: boolean;
  eventcubeSettings?: IEventcubeSettings;
};

interface IOnboardMediaSettings {
  type: "image" | "video";
  url: string;
}

export interface IUserCompany {
  role: string;
  company: ICompany;
}

export interface ICompanyCoupon {
  discount_percentage: number;
  id: string;
  name: string;
}

export interface ICompanyReduxState {
  company: ICompany | null;
  companies: IUserCompany[];
  companyPlan: ICompanyServicePlan | null;
  companySettings: ICompanySettings | null;
  isInternalCompany: boolean;
  multipleCompanyView: boolean;
  isLoading: boolean;
}

export interface ITag {
  name: string;
  slug: string;
  company: string;
  _id: string;
}
